import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { recipientsAPI, transactionsAPI, exchangeRateAPI, paymentsAPI, api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ArrowRight, Info, Smartphone } from 'lucide-react';

const currencies = ['USD', 'EUR', 'GBP', 'NOK', 'KES', 'ETB', 'SAR', 'AED', 'INR', 'CAD', 'AUD', 'JPY', 'CNY'];

export default function Send() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const [mpesaPhone, setMpesaPhone] = useState('');
  const [mpesaAmount, setMpesaAmount] = useState('');
  const [mpesaSending, setMpesaSending] = useState(false);

  useEffect(() => {
    loadRecipients();
  }, []);

  useEffect(() => {
    const to = searchParams.get('to');
    if (to) setToCurrency(to.toUpperCase());
    const from = searchParams.get('from');
    if (from) setFromCurrency(from.toUpperCase());
    const amt = searchParams.get('amount');
    if (amt && !isNaN(parseFloat(amt))) setAmount(amt);
  }, [searchParams]);

  useEffect(() => {
    if (amount && fromCurrency && toCurrency) {
      fetchExchangeRate();
    }
  }, [fromCurrency, toCurrency]);

  const loadRecipients = async () => {
    try {
      const response = await recipientsAPI.getAll();
      setRecipients(response.data);
    } catch (error) {
      toast.error('Failed to load recipients');
    }
  };

  const fetchExchangeRate = async () => {
    setCalculating(true);
    try {
      const response = await exchangeRateAPI.getRate(fromCurrency, toCurrency);
      setExchangeRate(response.data);
    } catch (error) {
      toast.error('Failed to fetch exchange rate');
    } finally {
      setCalculating(false);
    }
  };

  const calculateTotal = () => {
    if (!amount) return { fee: 0, total: 0, converted: 0 };
    const amountNum = parseFloat(amount);
    const fee = amountNum * 0.01;
    const total = amountNum + fee;
    const converted = exchangeRate ? amountNum * exchangeRate.rate : amountNum;
    return { fee, total, converted };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRecipient) {
      toast.error('Please select a recipient');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const txResponse = await transactionsAPI.initiate({
        recipient_id: selectedRecipient,
        amount: parseFloat(amount),
        from_currency: fromCurrency,
        to_currency: toCurrency
      });

      const paymentResponse = await paymentsAPI.createSession({
        transaction_id: txResponse.data.id,
        origin_url: window.location.origin
      });

      window.location.href = paymentResponse.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate transfer');
    } finally {
      setLoading(false);
    }
  };

  const { fee, total, converted } = calculateTotal();

  const handleMpesaSend = async () => {
    if (!mpesaPhone || !mpesaAmount || parseFloat(mpesaAmount) <= 0) {
      toast.error('Please enter phone number and valid amount');
      return;
    }
    setMpesaSending(true);
    try {
      const res = await api.post('/mpesa/send', {
        phone_number: mpesaPhone,
        amount: parseFloat(mpesaAmount),
        remarks: 'EzyTransfer M-Pesa send',
      });
      if (res.data.status === 'success') {
        toast.success(`Sent ETB ${mpesaAmount} to ${mpesaPhone} via M-Pesa`);
        setMpesaPhone('');
        setMpesaAmount('');
      } else {
        toast.error(res.data.response?.responseMessage || res.data.response?.error || 'M-Pesa send failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'M-Pesa send failed');
    } finally {
      setMpesaSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Send Money</h1>
          <p className="text-muted-foreground">Transfer money to your recipients worldwide</p>
        </div>

        <Tabs defaultValue="card" className="space-y-6">
          <TabsList>
            <TabsTrigger value="card" data-testid="send-tab-card">Card / Bank</TabsTrigger>
            <TabsTrigger value="mpesa" data-testid="send-tab-mpesa" className="gap-2">
              <Smartphone className="w-4 h-4" /> M-Pesa
            </TabsTrigger>
          </TabsList>

          <TabsContent value="card">
        <Card data-testid="send-money-form-card" className="p-8">
          {recipients.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">You need to add a recipient first</p>
              <Button data-testid="add-recipient-btn" onClick={() => navigate('/recipients')} variant="accent">
                Add Recipient
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="recipient">Select Recipient</Label>
                <Select data-testid="recipient-select" value={selectedRecipient} onValueChange={setSelectedRecipient}>
                  <SelectTrigger data-testid="recipient-select-trigger">
                    <SelectValue placeholder="Choose a recipient" />
                  </SelectTrigger>
                  <SelectContent>
                    {recipients.map((recipient) => (
                      <SelectItem key={recipient.id} value={recipient.id} data-testid={`recipient-option-${recipient.id}`}>
                        {recipient.name} - {recipient.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    data-testid="amount-input"
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="fromCurrency">From Currency</Label>
                  <Select value={fromCurrency} onValueChange={setFromCurrency}>
                    <SelectTrigger data-testid="from-currency-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((curr) => (
                        <SelectItem key={curr} value={curr}>
                          {curr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="toCurrency">To Currency</Label>
                <Select value={toCurrency} onValueChange={setToCurrency}>
                  <SelectTrigger data-testid="to-currency-select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((curr) => (
                      <SelectItem key={curr} value={curr}>
                        {curr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {amount && exchangeRate && (
                <Card data-testid="transfer-summary" className="p-6 bg-muted">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-4 h-4 text-accent" />
                    <h3 className="font-semibold">Transfer Summary</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-mono font-semibold" data-testid="summary-amount">${parseFloat(amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fee (1%):</span>
                      <span className="font-mono font-semibold" data-testid="summary-fee">${fee.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exchange Rate:</span>
                      <span className="font-mono font-semibold" data-testid="summary-rate">1 {fromCurrency} = {exchangeRate.rate.toFixed(4)} {toCurrency}</span>
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between">
                      <span className="font-semibold">Total to Pay:</span>
                      <span className="font-mono font-bold text-lg" data-testid="summary-total">${total.toFixed(2)} {fromCurrency}</span>
                    </div>
                    <div className="flex justify-between text-accent">
                      <span className="font-semibold">Recipient Gets:</span>
                      <span className="font-mono font-bold text-lg" data-testid="summary-converted">{converted.toFixed(2)} {toCurrency}</span>
                    </div>
                  </div>
                </Card>
              )}

              <Button data-testid="proceed-to-payment-btn" type="submit" className="w-full bg-accent hover:bg-accent/90 h-11" disabled={loading || calculating}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Proceed to Payment <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              
              <div className="text-center text-xs text-muted-foreground mt-2">
                <Info className="w-3 h-3 inline mr-1" />
                Demo mode: Use Stripe test cards (4242 4242 4242 4242)
              </div>
            </form>
          )}
        </Card>
          </TabsContent>

          <TabsContent value="mpesa">
            <Card data-testid="mpesa-send-card" className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Send via M-Pesa</h2>
                  <p className="text-sm text-muted-foreground">Send money directly to an Ethiopian M-Pesa number</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Recipient Phone Number</Label>
                  <Input
                    data-testid="mpesa-send-phone-input"
                    placeholder="251911234567"
                    value={mpesaPhone}
                    onChange={(e) => setMpesaPhone(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Ethiopian M-Pesa number (starting with 251)</p>
                </div>
                <div>
                  <Label>Amount (ETB)</Label>
                  <Input
                    data-testid="mpesa-send-amount-input"
                    type="number"
                    step="1"
                    placeholder="100"
                    value={mpesaAmount}
                    onChange={(e) => setMpesaAmount(e.target.value)}
                  />
                </div>
                <Button
                  data-testid="mpesa-send-submit-btn"
                  onClick={handleMpesaSend}
                  className="w-full bg-green-600 hover:bg-green-700 text-white h-11"
                  disabled={mpesaSending || !mpesaPhone || !mpesaAmount}
                >
                  {mpesaSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Send via M-Pesa <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <div className="text-center text-xs text-muted-foreground mt-2">
                  <Info className="w-3 h-3 inline mr-1" />
                  Powered by Safaricom Ethiopia M-Pesa
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}